import { distinctUntilChanged, debounceTime, startWith } from 'rxjs/operators';
import { CommonUtilsService } from './../shared/common-utils.service';
import { ControlsBaseComponent } from './../shared/components/controls-base.component';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  ControlContainer,
  FormArray,
  FormControl,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { Config } from '../models/ui-config';
import { JsonDynamicComponentService } from '../json-dynamic-component.service';
import { Subscription } from 'rxjs';
import { UiControlType } from '../enums/ui-control-type.enum';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

@Component({
  selector: 'ra-dynamic-list',
  templateUrl: './dynamic-list.component.html',
  styleUrls: ['./dynamic-list.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  standalone: false,
})
export class DynamicListComponent
  extends ControlsBaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];
  showSpinnerForDropdown = false;
  filterCtrl = new FormControl('');
  filteredValues: any[] = [];

  constructor(
    protected jsonDynamicComponentService: JsonDynamicComponentService,
    protected commonUtils: CommonUtilsService,
    protected cdref: ChangeDetectorRef,
  ) {
    super(jsonDynamicComponentService, commonUtils);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.filterCtrl.valueChanges
        .pipe(startWith(''))
        .subscribe(() => this.filterDropdownValues()),
    );
    if (
      this.currentConfig?.dictKeyConfig?.uiControlType ===
        UiControlType.Dropdown &&
      !this.currentConfig.dictKeyConfig?.values &&
      // In read-only mode the list entries are rendered from the saved value,
      // so fetching the option list is redundant. Keep fetching for
      // `dropdownID`-keyed lists, which need the options to map ids to labels.
      (!this.readOnly || this.currentConfig.dictKeyConfig?.dropdownID)
    ) {
      this.showSpinnerForDropdown = true;
      this.getDropdownValues(this.currentConfig.dictKeyConfig);
    }
    this.subscriptions[this.subscriptions.length] = (
      this.currentConfig?.control as FormArray
    )?.valueChanges
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((_value) => {
        this.onValueChanged();
      });

    if (
      this.currentConfig.control?.controls?.length === 1 &&
      this.currentConfig.control?.controls?.[0]?.value === null
    ) {
      this.currentConfig.control.removeAt(0);
    }
  }

  addDynamicListValue(formArray: FormArray) {
    // Use the full validator set from the JSON config (e.g. required + min)
    // so partial / invalid edits invalidate the form and prevent downstream
    // auto-refresh side-effects (like the custom-bucket call) from firing.
    const itemValidators =
      CommonUtilsService.getValidators(
        this.currentConfig?.dictKeyConfig?.validators,
      ) || [];
    const validators = itemValidators.length
      ? itemValidators
      : [Validators.required];
    formArray.push(new FormControl(null, validators));
  }

  deleteDynamicListValue(formArray: FormArray, index: number) {
    formArray.removeAt(index);
  }

  drop(event: CdkDragDrop<string[]>) {
    const formArray = this.currentConfig.control as FormArray;
    const control = formArray.at(event.previousIndex);
    formArray.removeAt(event.previousIndex);
    formArray.insert(event.currentIndex, control);
  }

  getDropdownValues(config: Config) {
    this.commonUtils
      .getDropdownValues(config, this.form?.value || {})
      .then((_res) => {
        this.showSpinnerForDropdown = false;
        this.filterDropdownValues();
      });
  }

  filterDropdownValues() {
    const search = (this.filterCtrl.value || '').toString().toLowerCase();
    const allValues = this.currentConfig?.dictKeyConfig?.values || [];
    if (!search) {
      this.filteredValues = allValues;
    } else {
      this.filteredValues = allValues.filter((v) =>
        v?.toString().toLowerCase().includes(search),
      );
    }
  }

  getValueFormControl(key: string | number) {
    return this.parentFormGroup
      ?.get(this.currentConfig.property?.toString())
      ?.get(key?.toString()) as FormControl;
  }

  ngOnDestroy() {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
  }
}
